import { Typography } from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  mergeKapGoalMeasuresWithKapMeasureAnnualReport,
  sortKapMeasures,
} from 'src/screens/shared/measure/utils/KapMeasureUtils'
import { KapGoalAnnualReport } from 'src/shared/constants/milestone-constants'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { ReadOnlySelection, ReadOnlyTextField } from 'src/shared/presentation/ReadOnly'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { resolveGoalRatingColor } from 'src/shared/utils/StylingUtils'
import { useClient } from 'urql'
import { KapMilestoneGoalRelatedMeasuresGrid } from './KapMilestoneGoalRelatedMeasuresGrid'
import { Query_Root } from 'src/@types/graphql'
import { queryKapAnnualReportAndGoalReportAndMeasures } from 'src/screens/kap/implementation/milestone/details/annual-report/goals/kapMilestoneGoalQueries'

interface Props {
  goalId: number
}

export const KapMilestoneGoalCardContent = ({ goalId }: Props): ReactElement => {
  const [goal, setGoal] = useState<KapGoalAnnualReport>()
  const { milestoneId: milestoneIdString } = useParams()
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const milestoneId = parseInt(milestoneIdString as string)

  const fetchKapGoalAnnualReportAndMeasuresByGoalAndMilestoneId = useCallback(async () => {
    const { data, error } = await urqlClient
      .query<
        {
          kap_goal_by_pk: Query_Root['kap_goal_by_pk']
          kap_goal_annual_report: Query_Root['kap_goal_annual_report']
          kap_measure_annual_report: Query_Root['kap_measure_annual_report']
        },
        { goalId: number; milestoneId: number }
      >(queryKapAnnualReportAndGoalReportAndMeasures, { goalId: goalId, milestoneId: milestoneId })
      .toPromise()

    if (
      error ||
      !data?.kap_goal_annual_report ||
      !data?.kap_goal_by_pk?.kap_goal_measures ||
      !data?.kap_measure_annual_report
    ) {
      throw new Error('Resource not found')
    }

    return {
      fetchedKapGoalAnnualReport: data.kap_goal_annual_report,
      fetchedKapGoalByPk: data.kap_goal_by_pk,
      fetchedKapGoalMeasures: data.kap_goal_by_pk.kap_goal_measures,
      fetchedKapMeasureAnnualReport: data.kap_measure_annual_report,
    }
  }, [goalId, milestoneId, urqlClient])

  useEffect(() => {
    const initData = async () => {
      try {
        const {
          fetchedKapGoalAnnualReport,
          fetchedKapGoalByPk,
          fetchedKapGoalMeasures,
          fetchedKapMeasureAnnualReport,
        } = await fetchKapGoalAnnualReportAndMeasuresByGoalAndMilestoneId()

        const mergedMeasuresAndReports = mergeKapGoalMeasuresWithKapMeasureAnnualReport(
          fetchedKapGoalMeasures,
          fetchedKapMeasureAnnualReport,
        )

        setGoal({
          goal_id: goalId,
          reason: fetchedKapGoalAnnualReport[0]?.reason ?? '',
          rating: fetchedKapGoalAnnualReport[0]?.rating ?? '',
          description: fetchedKapGoalByPk.description,
          goal_measures: sortKapMeasures(mergedMeasuresAndReports),
        })
      } catch (err) {
        notificationService.operationFailed()
      }
    }
    initData()
  }, [fetchKapGoalAnnualReportAndMeasuresByGoalAndMilestoneId, goalId, notificationService])

  return (
    <>
      {goal && (
        <>
          <ReadOnlyTextField text={getMessage('label.goal.rating.description.kap')}>
            {goal.description && <HtmlRenderer html={goal.description} />}
          </ReadOnlyTextField>
          <ReadOnlyTextField text={getMessage('label.goal.rating')}>
            {goal.rating && (
              <Typography color={resolveGoalRatingColor(goal.rating)}>
                {goal.rating && getMessage(`label.goal.rating.${goal.rating}`)}
              </Typography>
            )}
          </ReadOnlyTextField>
          <ReadOnlyTextField text={getMessage('label.goal.rating.reason.kap')}>
            {goal.reason && <HtmlRenderer html={goal.reason} />}
          </ReadOnlyTextField>
          <ReadOnlySelection text={getMessage('label.related.measures')} isLast>
            <KapMilestoneGoalRelatedMeasuresGrid measures={goal.goal_measures ?? []} />
          </ReadOnlySelection>
        </>
      )}
    </>
  )
}
